import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useFetch } from '@reveni-io/reveni-ui'

import useWebsocketAction from '@/common/hooks/useWebsocketAction/useWebsocketAction'
import { integrationStatuses } from '@/configuration/pages/ConfigurationPage/IntegrationsConfigurations/constants'
import configurationServices from '@/configuration/services/services'
import routes from '@/Router/routes.constants'

import { defaultFulfillmentTrigger } from '../constants'
import { findIntegrationByType, isSeelInstallationUnreachable } from '../helpers'
import {
  transformRequestCreateSeelIntegration,
  transformResponseCreateSeelIntegration,
  transformResponseIntegrationsInstallStatus,
} from './transformers'

const useSeelActivation = ({ integration, storeId }) => {
  const [error, setError] = useState(false)
  const [installStatus, setInstallStatus] = useState(null)
  const [websocketUrl, setWebsocketUrl] = useState(integration.websocket_url)
  const [connectionLost, setConnectionLost] = useState(false)

  const { fetchData: createIntegration, loading } = useFetch(configurationServices.createIntegration, {
    doInitialRequest: false,
    transformRequest: transformRequestCreateSeelIntegration,
    transformResponse: transformResponseCreateSeelIntegration,
  })

  const { fetchData: getIntegrations, loading: checkingInstallStatus } = useFetch(
    configurationServices.getIntegrations,
    {
      doInitialRequest: false,
      transformResponse: transformResponseIntegrationsInstallStatus,
    }
  )

  const navigate = useNavigate()

  const applyInstallStatus = status => {
    if (status === integrationStatuses.installed) {
      navigate(routes.configurationPage.integrations)

      return
    }

    setInstallStatus(status)
    setError(status === integrationStatuses.rejected)
  }

  const onInstallFinished = message => {
    setWebsocketUrl(null)
    applyInstallStatus(message.data.status)
  }

  const onConnectionLost = () => {
    setWebsocketUrl(null)
    setConnectionLost(true)
  }

  const onInstallFailed = message => {
    if (!message) {
      onConnectionLost()

      return
    }

    setWebsocketUrl(null)
    setInstallStatus(integrationStatuses.failed)
  }

  useWebsocketAction({
    websocketUrl,
    onComplete: onInstallFinished,
    onError: onInstallFailed,
    onClose: onConnectionLost,
  })

  const checkInstallStatus = async () => {
    try {
      const integrations = await getIntegrations({ storeId })
      const currentIntegration = findIntegrationByType(integrations, integration.type)

      if (isSeelInstallationUnreachable(currentIntegration)) return

      setConnectionLost(false)
      applyInstallStatus(currentIntegration.status)
      setWebsocketUrl(currentIntegration.websocket_url)
    } catch {
      setConnectionLost(true)
    }
  }

  const activate = async () => {
    setError(false)
    setConnectionLost(false)

    try {
      const response = await createIntegration({
        storeId,
        type: integration.type,
        fulfillmentTrigger: defaultFulfillmentTrigger,
      })

      setInstallStatus(integrationStatuses.pending)
      setWebsocketUrl(response.websocket_url)
    } catch {
      setError(true)
    }
  }

  return {
    activate,
    checkInstallStatus,
    loading,
    checkingInstallStatus,
    error,
    connectionLost,
    status: installStatus ?? integration.status,
  }
}

export default useSeelActivation
